import {
  Button,
  Input,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";
import React, { useMemo, useState } from "react";
import { AbbreviatedPopover } from "@/components/AbbreviatedPopover";
import "./styles.scss";

interface OwnProps {
  title: string;
  users: AccessUserItem[];
  open: boolean;
  loading?: boolean;
  showStates?: boolean;
  showPermissions?: boolean;
  onClose: () => void;
}

type Props = OwnProps;

export interface AccessUserItem {
  name: string;
  email: string;
  loading: boolean;
  regionNames?: string[];
  branchNames?: string[];
  stateCodes?: string[];
  readAccess?: boolean;
  writeAccess?: boolean;
  eligibilityAccess?: boolean;
}

const AccessListModalFC: React.FC<Props> = ({ title, users, open, onClose, loading, showPermissions, showStates }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    return users.filter(user => {
      const values = [
        user.name,
        user.email,
        ...(user.regionNames || []),
        ...(user.branchNames || []),
        ...(user.stateCodes || []),
      ];
      return values.some(v => v?.toLowerCase()?.includes(searchTerm.toLowerCase()));
    });
  }, [users, searchTerm]);

  return (
    <Modal open={open} onClose={onClose} size={"fullscreen"} className={"accessListModal"}>
      <Modal.Header>{title}</Modal.Header>
      <Modal.Content>
        <Input
          loading={loading}
          placeholder="Search..."
          value={searchTerm}
          className={"search"}
          onChange={(_, data) => setSearchTerm(data.value)}
        />
        <div className={"tableContainer"}>
          <Table compact>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Region</TableHeaderCell>
                <TableHeaderCell>Branch</TableHeaderCell>
                {showStates && <TableHeaderCell>States</TableHeaderCell>}
                {showPermissions && (
                  <>
                    <TableHeaderCell>View Prompt</TableHeaderCell>
                    <TableHeaderCell>Edit Prompt</TableHeaderCell>
                    <TableHeaderCell>Edit Eligibility</TableHeaderCell>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user, index) => {
                return (
                  <TableRow key={`access-user-${index}`}>
                    <TableCell>
                      <AbbreviatedPopover text={user.name} />
                    </TableCell>
                    <TableCell>
                      <AbbreviatedPopover text={user.email} maxLength={50} />
                    </TableCell>
                    <TableCell>
                      <AbbreviatedPopover text={user.regionNames?.join(", ")} />
                    </TableCell>
                    <TableCell>
                      <AbbreviatedPopover text={user.branchNames?.join(", ")} />
                    </TableCell>
                    {showStates && (
                      <TableCell>
                        <AbbreviatedPopover text={user.stateCodes?.join(", ")} />
                      </TableCell>
                    )}
                    {showPermissions && (
                      <>
                        <TableCell>{user.readAccess ? "✓" : "✕"}</TableCell>
                        <TableCell>{user.writeAccess ? "✓" : "✕"}</TableCell>
                        <TableCell>{user.eligibilityAccess ? "✓" : "✕"}</TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" onClick={onClose} secondary>
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export const AccessListModal = AccessListModalFC;
